import React, { useRef, useState, useEffect, useLayoutEffect, useContext, useMemo } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import logo from "./Pictures/logo.png";
import styles from "../Modules/Nav.module.css";
import ShowIf from "../Components/ShowIf.js";
import { SessionBadge } from "../Components/SessionExpiry.jsx";
import useUser from "../Util/useUser.js";
import usePermission from "../Util/usePermissions";
import useWhatsAppLive from "../hooks/useWhatsAppLive.js";
import { NavTrailContext, registerNavItem, useNavItems, findNavItem, nodeText } from "../Util/navRegistry.js";
import { openCommandPalette } from "../Components/CommandPalette.jsx";

/** True once any visible menu item is registered under `trail`. */
function useHasNavChild(trail) {
    const items = useNavItems();
    return items.some((it) => trail.every((t, i) => it.trail[i] === t));
}

/** Trail of the menu entry the current page belongs to (for active highlights). */
const ActiveTrailContext = React.createContext([]);

/** Single dropdown item — used for every nav group */
const NavDropdown = ({ name, label, children, isOpen, onToggle, right }) => {
    const ref = useRef(null);
    const id = name || label;
    const activeTrail = useContext(ActiveTrailContext);
    const isActive = activeTrail[0] === id;
    const trail = useMemo(() => [id], [id]);
    const hasChild = useHasNavChild(trail);

    // Hover-open on desktop. Closing waits a moment after the pointer leaves, so
    // a diagonal move from the button into its menu (or across the small gap)
    // doesn't drop the menu; it only closes this menu if it is still the open one.
    const closeTimer = useRef(null);
    const hoverOpenedAt = useRef(0);
    const buttonRef = useRef(null);
    useEffect(() => () => clearTimeout(closeTimer.current), []);
    const onEnter = (e) => {
        if (e.pointerType !== "mouse") return;
        clearTimeout(closeTimer.current);
        if (!isOpen) { hoverOpenedAt.current = Date.now(); onToggle(id); }
    };
    // Most mouse users point AND click: the hover already opened the menu, so
    // a click right after must not toggle it shut again.
    const onButtonClick = () => {
        if (isOpen && Date.now() - hoverOpenedAt.current < 400) return;
        onToggle(id);
    };

    // Escape closes the open menu and puts focus back on its button.
    useEffect(() => {
        if (!isOpen) return undefined;
        const onKey = (e) => {
            if (e.key !== "Escape") return;
            onToggle(null);
            buttonRef.current?.focus();
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [isOpen, onToggle]);
    const onLeave = (e) => {
        if (e.pointerType !== "mouse") return;
        clearTimeout(closeTimer.current);
        closeTimer.current = setTimeout(() => onToggle({ closeIf: id }), 180);
    };

    // Close on outside click — needed for mobile (no hover)
    useEffect(() => {
        if (!isOpen) return;
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) onToggle(null);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [isOpen, onToggle]);

    return (
        <li
            className={`nav-item ${styles.navItem}`}
            hidden={!hasChild}
            ref={ref}
            onPointerEnter={onEnter}
            onPointerLeave={onLeave}
        >
            <button
                ref={buttonRef}
                className={`nav-link ${styles.dropBtn} ${isActive ? styles.dropBtnActive : ""}`}
                onClick={onButtonClick} // mobile: open on tap
                aria-expanded={isOpen}
            >
                {label}{" "}
                <span className={styles.caret}>{isOpen ? "▲" : "▼"}</span>
            </button>
            {/* Always mounted (hidden when closed) so every permitted item
                registers with the command palette / active highlight. */}
            <ul className={`dropdown-menu ${isOpen ? "show" : ""} ${styles.dropMenu} ${right ? styles.dropMenuRight : ""}`}>
                <NavTrailContext.Provider value={trail}>{children}</NavTrailContext.Provider>
            </ul>
        </li>
    );
};

/** Wrapper so ShowIf can wrap a <li>. Registers itself (path, label, group
 *  trail) while mounted, i.e. only when the user may open it. */
const NavItem = ({ to, children, onClick }) => {
    const trail = useContext(NavTrailContext);
    // Badges/icons aren't part of the name: keep only the text children
    const label = React.Children.toArray(children)
        .filter((c) => typeof c === "string" || typeof c === "number")
        .join("").trim();
    const trailKey = trail.join("\u0000");
    useEffect(
        () => registerNavItem({ to, label, trail: trailKey.split("\u0000") }),
        [to, label, trailKey]
    );
    return (
        <li>
            <NavLink className="dropdown-item" activeClassName={styles.itemActive} exact to={to} onClick={onClick}>
                {children}
            </NavLink>
        </li>
    );
};

/**
 * Flyout sub-menu that opens to the side on desktop and inline on mobile.
 * `name` = the plain-text group name for the trail (page eyebrow, active
 * highlight) when `label` is a node, e.g. one carrying an unread badge.
 */
const NavDropRight = ({ label, name, children, openLeft, permission, any: anyPerms, all: allPerms }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [flipLeft, setFlipLeft] = useState(false);
    const parentTrail = useContext(NavTrailContext);
    const trailName = name ?? (typeof label === "string" ? label : nodeText(label));
    const trail = useMemo(() => [...parentTrail, trailName], [parentTrail, trailName]);
    const activeTrail = useContext(ActiveTrailContext);
    const isActive = trail.every((t, i) => activeTrail[i] === t);
    const containerRef = useRef(null);
    const menuRef = useRef(null);
    const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermission();

    // On open, flip the flyout to the left if it would overflow the viewport's right edge.
    useLayoutEffect(() => {
        if (!isOpen || !containerRef.current || !menuRef.current) return;
        // Desktop only; mobile renders the flyout inline (static) so direction is irrelevant.
        if (window.innerWidth <= 991.98) return;
        const rect = containerRef.current.getBoundingClientRect();
        const menuWidth = menuRef.current.offsetWidth || 200;
        setFlipLeft(rect.right + menuWidth > window.innerWidth - 8);
    }, [isOpen]);

    const openLeftFinal = openLeft || flipLeft;

    // Hidden when none of its items is visible to this user. Items gate
    // themselves (ShowIf) and register while mounted, so the menu stays
    // mounted and asks the registry. The old hand-kept any=[…] lists drifted
    // from their children and hid ~45 pages people were allowed to open.
    // `permission` / `all` (and `any`) still narrow a section explicitly.
    const hasChild = useHasNavChild(trail);
    let gateOk = true;
    if (permission) gateOk = gateOk && hasPermission(permission);
    if (allPerms)   gateOk = gateOk && hasAllPermissions(allPerms);
    void anyPerms; void hasAnyPermission; // superseded by hasChild
    const showSection = gateOk && hasChild;

    return (
        <li
            ref={containerRef}
            hidden={!showSection}
            className={styles.dropRightContainer}
            onPointerEnter={(e) => { if (e.pointerType === "mouse") setIsOpen(true); }}
            onPointerLeave={(e) => { if (e.pointerType === "mouse") setIsOpen(false); }}
        >
            <button
                className={`${styles.dropRightBtn} ${isActive ? styles.dropRightBtnActive : ""}`}
                onClick={(e) => {
                    e.preventDefault();
                    setIsOpen(!isOpen);
                }}
                aria-expanded={isOpen}
            >
                {label}
                <span className={styles.dropRightArrow}>▶</span>
            </button>
            <ul
                ref={menuRef}
                className={`${styles.dropRightMenu} ${openLeftFinal ? styles.dropLeftMenu : ""} ${isOpen ? styles.dropRightMenuOpen : ""}`}
            >
                <NavTrailContext.Provider value={trail}>{children}</NavTrailContext.Provider>
            </ul>
        </li>
    );
};

const Nav = ({ loggedIn, logout }) => {
    const [menuOpen, setMenuOpen] = useState(false); // mobile hamburger
    const [openDrop, setOpenDrop] = useState(null); // which desktop dropdown is open
    const { user } = useUser();
    const cartonMode = user?.yarnCartonModeEnabled ?? false;
    const { hasPermission } = usePermission();
    // Unread WhatsApp chats → a green count on Sales › WhatsApp › Inbox.
    const { unread: waUnread } = useWhatsAppLive();
    const waBadge = waUnread.chats > 0
        ? <span className={styles.waBadge} aria-label={`${waUnread.chats} unread WhatsApp chats`}>{waUnread.chats}</span>
        : null;
    // Deny-flag: restricted users lose agent-wise / party-wise reports entirely.
    const restrictSeg = hasPermission("reports:restrict_segregation");

    const closeMobile = () => setMenuOpen(false);
    const closeAll = () => {
        setOpenDrop(null);
        closeMobile();
    };
    // toggle(name) opens/closes that menu; toggle(null) closes all;
    // toggle({ closeIf: name }) closes it only if it is still the open one
    // (a delayed hover-close must not shut the menu the pointer moved to).
    const toggle = (name) =>
        setOpenDrop((prev) => {
            if (name && typeof name === "object") return prev === name.closeIf ? null : prev;
            return prev === name ? null : name;
        });

    // Which menu entry does the current page belong to? Drives the coral
    // highlight on its top-level group and flyout.
    const { pathname } = useLocation();
    const navItems = useNavItems();
    const activeTrail = useMemo(() => findNavItem(pathname, navItems)?.trail || [], [pathname, navItems]);

    return (
        <ActiveTrailContext.Provider value={activeTrail}>
        <nav className={`navbar navbar-expand-lg ${styles.navbar}`}>
            <div className="container-fluid px-3 px-md-4">
                {/* Logo */}
                <Link className="navbar-brand p-0" to="/" onClick={closeAll}>
                    <img
                        src={logo}
                        alt="Company Logo"
                        className={styles.logo}
                        width="auto"
                    />
                </Link>

                {/* Go to any page (Ctrl/⌘ K) */}
                {loggedIn && (
                    <button
                        type="button"
                        className={`${styles.searchBtn} order-lg-last`}
                        onClick={() => { closeAll(); openCommandPalette(); }}
                        aria-label="Go to a page"
                        title="Go to a page (Ctrl K)"
                    >
                        <i className="bi bi-search" aria-hidden="true" />
                        <span className={styles.searchLabel}>Go to…</span>
                        <kbd className={styles.searchKbd}>{/Mac|iPhone|iPad/.test(navigator.platform) ? "⌘K" : "Ctrl K"}</kbd>
                    </button>
                )}

                {/* Hamburger */}
                <button
                    className="navbar-toggler"
                    type="button"
                    onClick={() => setMenuOpen((o) => !o)}
                    aria-expanded={menuOpen}
                    aria-label="Toggle navigation"
                >
                    <span className="navbar-toggler-icon" />
                </button>

                {/* Links */}
                <div
                    className={`navbar-collapse ${menuOpen ? "show" : "collapse"}`}
                >
                    <ul className="navbar-nav ms-auto align-items-lg-center gap-lg-1 py-2 py-lg-0">
                        {/* ── Production ── */}
                        <>
                            <NavDropdown
                                label="Production"
                                isOpen={openDrop === "Production"}
                                onToggle={toggle}
                            >
                                <NavDropRight label="Orders" any={["orderbook:view", "productionorder:view", "productionorder:producedreport", "productionorder:grade", "secondsbatch:view", "defectreport:view", "defectcode:view", "defectcode:manage"]}>
                                    <ShowIf permission="orderbook:view">
                                        <NavItem to="/orderbook" onClick={closeAll}>
                                            Order Book
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="productionorder:view">
                                        <NavItem to="/productionorders" onClick={closeAll}>
                                            Production Order
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="productionorder:producedreport">
                                        <NavItem to="/productionorders/completed" onClick={closeAll}>
                                            Completed Orders
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="productionorder:grade">
                                        <NavItem to="/productionorder/ungraded-inventory" onClick={closeAll}>
                                            Ungraded Inventory
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="secondsbatch:view">
                                        <NavItem to="/secondsbatch" onClick={closeAll}>
                                            Seconds (Defective) Batches
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="defectreport:view">
                                        <NavItem to="/defect-pareto" onClick={closeAll}>
                                            Defect Pareto
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf any={["defectcode:view", "defectcode:manage"]}>
                                        <NavItem to="/defectcode" onClick={closeAll}>
                                            Defect Codes
                                        </NavItem>
                                    </ShowIf>
                                </NavDropRight>
                                <NavDropRight label="Machine" any={["machine:view", "loomshade:view", "operator:view", "machineprogram:view", "runselection:view", "machineprogram:rollcomplete", "efficiencyreport:view", "warpbeam:view", "flowboard:view", "sop:view", "sop:manage"]}>
                                    <ShowIf permission="machine:view">
                                        <NavItem to="/machine" onClick={closeAll}>
                                            Machine
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="warpbeam:view">
                                        <NavItem to="/warpbeam" onClick={closeAll}>
                                            Warping / Beams
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="loomshade:view">
                                        <NavItem to="/loomshade" onClick={closeAll}>
                                            Loom Shade
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="operator:view">
                                        <NavItem to="/operator" onClick={closeAll}>
                                            Operator
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="machineprogram:view">
                                        <NavItem to="/machineprogram/" onClick={closeAll}>
                                            Machine Program
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="runselection:view">
                                        <NavItem to="/machineprogram/run-selection" onClick={closeAll}>
                                            Program Run Selection
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="machineprogram:rollcomplete">
                                        <NavItem to="/machineprogram/roll-completion-list" onClick={closeAll}>
                                            Cloth Roll Completion
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="efficiencyreport:view">
                                        <NavItem to="/efficiency-report" onClick={closeAll}>
                                            Efficiency Report
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="efficiencyreport:view">
                                        <NavItem to="/efficiency-report/operator-performance" onClick={closeAll}>
                                            Operator Performance
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="flowboard:view">
                                        <NavItem to="/flowboard" onClick={closeAll}>
                                            Production Flow (Bottleneck)
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf any={["sop:view", "sop:manage"]}>
                                        <NavItem to="/sop/templates" onClick={closeAll}>
                                            SOP Checklists
                                        </NavItem>
                                    </ShowIf>
                                </NavDropRight>
                                <ShowIf permission="sample:view">
                                    <NavItem to="/sample" onClick={closeAll}>
                                        Sample
                                    </NavItem>
                                </ShowIf>
                            </NavDropdown>
                        </>

                        {/* ── Design ── */}
                        <>
                            <NavDropdown
                                label="Design"
                                isOpen={openDrop === "Design"}
                                onToggle={toggle}
                            >
                                <NavDropRight label="Design & Costing" any={["quality:view", "design:view", "designcosting:view", "valueaddition:view"]}>
                                    <ShowIf permission="quality:view">
                                        <NavItem to="/quality" onClick={closeAll}>
                                            Quality
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="design:view">
                                        <NavItem to="/design" onClick={closeAll}>
                                            Design
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="designcosting:view">
                                        <NavItem to="/design/cost" onClick={closeAll}>
                                            Design Cost
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="valueaddition:view">
                                        <NavItem to="/valueaddition" onClick={closeAll}>
                                            Value Addition
                                        </NavItem>
                                    </ShowIf>
                                </NavDropRight>
                                <NavDropRight label="Catalogue" any={["catalogue:view", "cataloguematching:view", "cataloguesalescolour:view", "sareecategory:view", "photoshoot:view", "templateset:view", "salesset:view"]}>
                                    <ShowIf permission="catalogue:view">
                                        <NavItem to="/catalogue" onClick={closeAll}>
                                            Catalogue
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="sareecategory:view">
                                        <NavItem to="/sareecategory" onClick={closeAll}>
                                            Saree Category
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="cataloguematching:view">
                                        <NavItem to="/cataloguematching" onClick={closeAll}>
                                            Catalogue Matching
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="cataloguesalescolour:view">
                                        <NavItem to="/cataloguesalecolours" onClick={closeAll}>
                                            Sale Colours
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="photoshoot:view">
                                        <NavItem to="/photoshoot" onClick={closeAll}>
                                            Photoshoots
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="templateset:view">
                                        <NavItem to="/settemplate" onClick={closeAll}>
                                            Set Templates
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="salesset:view">
                                        <NavItem to="/salesset" onClick={closeAll}>
                                            Sales Linked Sets
                                        </NavItem>
                                    </ShowIf>
                                </NavDropRight>
                                <NavDropRight label="Weaving" any={["matching:view", "matchingcode:view", "harness:view", "warppattern:view", "warpdesigncolour:view"]}>
                                    <ShowIf permission="matching:view">
                                        <NavItem to="/matching" onClick={closeAll}>
                                            Matching
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="matchingcode:view">
                                        <NavItem to="/matchingcode" onClick={closeAll}>
                                            Matching Code
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="harness:view">
                                        <NavItem to="/harness" onClick={closeAll}>
                                            Harness
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="warppattern:view">
                                        <NavItem to="/warppattern" onClick={closeAll}>
                                            Warp Pattern
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="warpdesigncolour:view">
                                        <NavItem to="/warpdesigncolour" onClick={closeAll}>
                                            Warp Design Colour
                                        </NavItem>
                                    </ShowIf>
                                </NavDropRight>
                            </NavDropdown>
                        </>

                        {/* ── Purchase ── */}
                        <>
                            <NavDropdown
                                label="Purchase"
                                isOpen={openDrop === "Purchase"}
                                onToggle={toggle}
                            >
                                <NavDropRight label="Orders" any={["purchaseorder:view", "petaorder:view"]}>
                                    <ShowIf permission="purchaseorder:view">
                                        <NavItem to="/purchaseorder" onClick={closeAll}>
                                            Purchase Orders
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="petaorder:view">
                                        <NavItem to="/petaorder" onClick={closeAll}>
                                            Peta Orders
                                        </NavItem>
                                    </ShowIf>
                                </NavDropRight>
                                <NavDropRight label="Receipts" any={["purchasechallan:view", "purchasebill:view"]}>
                                    <ShowIf permission="purchasechallan:view">
                                        <NavItem to="/purchasechallan" onClick={closeAll}>
                                            Purchase Challan
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="purchasebill:view">
                                        <NavItem to="/purchasebill" onClick={closeAll}>
                                            Purchase Bill
                                        </NavItem>
                                    </ShowIf>
                                </NavDropRight>
                            </NavDropdown>
                        </>

                        {/* ── Sales ── */}
                        <>
                            <NavDropdown
                                name="Sales"
                                label={<>Sales{waBadge}</>}
                                isOpen={openDrop === "Sales"}
                                onToggle={toggle}
                            >
                                <NavDropRight label="Orders" permission="salesorder:view">
                                    <ShowIf permission="salesorder:view">
                                        <NavItem to="/salesorder" onClick={closeAll}>
                                            Sales Order
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="salesorder:view">
                                        <NavItem to="/salesorder/pick-list" onClick={closeAll}>
                                            Pick List by Rack
                                        </NavItem>
                                    </ShowIf>
                                </NavDropRight>
                                <NavDropRight label="Billing" any={["saleschallan:view", "salesbill:view", "salesreturn:view"]}>
                                    <ShowIf permission="saleschallan:view">
                                        <NavItem to="/saleschallan" onClick={closeAll}>
                                            Sales Challan
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="salesbill:view">
                                        <NavItem to="/salesbill" onClick={closeAll}>
                                            Sales Bill
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="salesreturn:view">
                                        <NavItem to="/salesreturn" onClick={closeAll}>
                                            Goods Return
                                        </NavItem>
                                    </ShowIf>
                                </NavDropRight>
                                <NavDropRight label={<>WhatsApp{waBadge}</>} name="WhatsApp" any={["whatsapp:send", "whatsapp:send_document"]}>
                                    <ShowIf permission="whatsapp:send">
                                        <NavItem to="/whatsapp/inbox" onClick={closeAll}>
                                            Inbox{waBadge}
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="salesbill:view">
                                        <NavItem to="/whatsapp/send-invoices" onClick={closeAll}>
                                            Send Invoices
                                        </NavItem>
                                        <NavItem to="/whatsapp/send-lr" onClick={closeAll}>
                                            Send Transport Receipts
                                        </NavItem>
                                    </ShowIf>
                                </NavDropRight>
                            </NavDropdown>
                        </>

                        {/* ── Yarn ── */}
                        <>
                            <NavDropdown
                                label="Yarn"
                                isOpen={openDrop === "Yarn"}
                                onToggle={toggle}
                            >
                                <NavDropRight label="Masters" any={["yarnquality:view", "yarnshade:view", "yarnprice:view"]}>
                                    <ShowIf permission="yarnquality:view">
                                        <NavItem to="/yarnquality" onClick={closeAll}>
                                            Yarn Quality
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="yarnshade:view">
                                        <NavItem to="/yarnshade" onClick={closeAll}>
                                            Yarn Shade
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="yarnprice:view">
                                        <NavItem to="/yarn/price" onClick={closeAll}>
                                            Yarn Price
                                        </NavItem>
                                    </ShowIf>
                                </NavDropRight>
                                {!cartonMode && (
                                    <NavDropRight label="Stock" permission="yarn_inventory:view">
                                        <ShowIf permission="yarn_inventory:view">
                                            <NavItem to="/yarn-inventory/stock" onClick={closeAll}>
                                                Yarn Stock
                                            </NavItem>
                                        </ShowIf>
                                        <ShowIf permission="yarn_inventory:view">
                                            <NavItem to="/yarn-inventory/available-stock" onClick={closeAll}>
                                                Available Stock
                                            </NavItem>
                                        </ShowIf>
                                        <ShowIf permission="yarn_inventory:view">
                                            <NavItem to="/yarn-inventory/pending-approvals" onClick={closeAll}>
                                                Pending Approvals
                                            </NavItem>
                                        </ShowIf>
                                    </NavDropRight>
                                )}
                                {!cartonMode && (
                                    <NavDropRight label="Transactions" any={["yarn_inventory:create", "yarn_consumption:view", "yarn_adjustment:view"]}>
                                        <ShowIf permission="yarn_inventory:create">
                                            <NavItem to="/yarn-inventory/receipt/add" onClick={closeAll}>
                                                Register Receipt
                                            </NavItem>
                                        </ShowIf>
                                        <ShowIf permission="yarn_inventory:create">
                                            <NavItem to="/yarn-inventory/transfer/add" onClick={closeAll}>
                                                New Transfer
                                            </NavItem>
                                        </ShowIf>
                                        <ShowIf permission="yarn_consumption:view">
                                            <NavItem to="/yarn-inventory/consumption" onClick={closeAll}>
                                                Yarn Consumption
                                            </NavItem>
                                        </ShowIf>
                                        <ShowIf permission="yarn_adjustment:view">
                                            <NavItem to="/yarn-inventory/adjustment" onClick={closeAll}>
                                                Yarn Adjustments
                                            </NavItem>
                                        </ShowIf>
                                        <ShowIf permission="yarn_inventory:create">
                                            <NavItem to="/yarn-inventory/supplier-return/add" onClick={closeAll}>
                                                Supplier Return
                                            </NavItem>
                                        </ShowIf>
                                        <ShowIf permission="yarn_inventory:create">
                                            <NavItem to="/yarn-inventory/sale/add" onClick={closeAll}>
                                                Yarn Sale
                                            </NavItem>
                                        </ShowIf>
                                    </NavDropRight>
                                )}
                                {!cartonMode && (
                                    <NavDropRight label="Cartons" any={["yarn_inventory:view", "cartoon:mark-inactive"]}>
                                        <ShowIf permission="yarn_inventory:view">
                                            <NavItem to="/yarn-inventory/cartoons" onClick={closeAll}>
                                                Yarn Cartons
                                            </NavItem>
                                        </ShowIf>
                                        <ShowIf permission="yarn_inventory:view">
                                            <NavItem to="/yarn-inventory/cartoon/scan" onClick={closeAll}>
                                                Scan Carton
                                            </NavItem>
                                        </ShowIf>
                                        <ShowIf permission="cartoon:mark-inactive">
                                            <NavItem to="/cartoonlocationaudit/add" onClick={closeAll}>
                                                Carton Location Audit
                                            </NavItem>
                                        </ShowIf>
                                        <ShowIf permission="cartoon:mark-inactive">
                                            <NavItem to="/cartoonqualityshadeaudit/add" onClick={closeAll}>
                                                Carton Quality/Shade Audit
                                            </NavItem>
                                        </ShowIf>
                                        <ShowIf permission="yarn_inventory:view">
                                            <NavItem to="/yarn-inventory/carton-reorder" onClick={closeAll}>
                                                Reorder / Low Stock
                                            </NavItem>
                                        </ShowIf>
                                    </NavDropRight>
                                )}
                                {!cartonMode && (
                                    <NavDropRight label="Yarn Reports" any={["yarn_inventory:view"]}>
                                        <ShowIf permission="yarn_inventory:view">
                                            <NavItem to="/yarn-inventory/reports/shade-summary" onClick={closeAll}>
                                                Shade Summary
                                            </NavItem>
                                        </ShowIf>
                                        <ShowIf permission="yarn_inventory:view">
                                            <NavItem to="/yarn-inventory/reports/weekly-consumption" onClick={closeAll}>
                                                Weekly Consumption
                                            </NavItem>
                                        </ShowIf>
                                        <ShowIf permission="yarn_inventory:view">
                                            <NavItem to="/yarn-inventory/reports/monthly-purchases" onClick={closeAll}>
                                                Monthly Purchases
                                            </NavItem>
                                        </ShowIf>
                                        <ShowIf permission="yarn_inventory:view">
                                            <NavItem to="/yarn-inventory/reports/movement" onClick={closeAll}>
                                                Yarn Movement
                                            </NavItem>
                                        </ShowIf>
                                        <ShowIf permission="yarn_inventory:view">
                                            <NavItem to="/yarn-inventory/run-rate" onClick={closeAll}>
                                                Run Rate
                                            </NavItem>
                                        </ShowIf>
                                    </NavDropRight>
                                )}
                                {cartonMode && (
                                    <NavDropRight label="Carton" any={["yarn_inventory:view", "yarnshade:sequence", "yarn_carton:adjust", "reports:yarncartonstock"]}>
                                        <ShowIf permission="yarn_inventory:view">
                                            <NavItem to="/yarn-inventory/carton-stock" onClick={closeAll}>
                                                Carton Stock
                                            </NavItem>
                                        </ShowIf>
                                        <ShowIf permission="yarn_inventory:view">
                                            <NavItem to="/yarn-inventory/carton-consumption" onClick={closeAll}>
                                                Consumption
                                            </NavItem>
                                        </ShowIf>
                                        <ShowIf permission="yarn_inventory:view">
                                            <NavItem to="/yarn-inventory/carton-adjustment" onClick={closeAll}>
                                                Adjustments
                                            </NavItem>
                                        </ShowIf>
                                        <ShowIf permission="yarn_inventory:view">
                                            <NavItem to="/yarn-inventory/carton-reorder" onClick={closeAll}>
                                                Reorder / Low Stock
                                            </NavItem>
                                        </ShowIf>
                                        <ShowIf permission="reports:yarncartonstock">
                                            <NavItem to="/yarn-inventory/reports/carton-day-summary" onClick={closeAll}>
                                                Day Summary
                                            </NavItem>
                                        </ShowIf>
                                        <ShowIf permission="yarn_carton:adjust">
                                            <NavItem to="/yarn-inventory/carton-defaults" onClick={closeAll}>
                                                Default Carton Weights
                                            </NavItem>
                                        </ShowIf>
                                        <ShowIf permission="yarnshade:sequence">
                                            <NavItem to="/yarn-inventory/shade-sequence" onClick={closeAll}>
                                                Shade Sequence (Admin)
                                            </NavItem>
                                        </ShowIf>
                                    </NavDropRight>
                                )}
                            </NavDropdown>
                        </>

                        {/* ── Accounting ── */}
                        <>
                            <NavDropdown
                                label="Accounting"
                                isOpen={openDrop === "Accounting"}
                                onToggle={toggle}
                            >
                                <ShowIf permission="payment_voucher:create">
                                    <NavItem to="/paymentvoucher/add" onClick={closeAll}>
                                        New Voucher / Transfer
                                    </NavItem>
                                </ShowIf>
                                <ShowIf permission="payment_voucher:create">
                                    <NavItem to="/paymentvoucher/add?mode=transfer" onClick={closeAll}>
                                        Record Transfer
                                    </NavItem>
                                </ShowIf>
                                <ShowIf permission="journal_voucher:create">
                                    <NavItem to="/journalvoucher/add" onClick={closeAll}>
                                        New Journal Voucher
                                    </NavItem>
                                </ShowIf>
                                <li><hr className="dropdown-divider" /></li>
                                <ShowIf permission="payment_voucher:view">
                                    <NavItem to="/paymentvoucher" onClick={closeAll}>
                                        Payment Vouchers
                                    </NavItem>
                                </ShowIf>
                                <ShowIf permission="payment_voucher:view">
                                    <NavItem to="/paymentvoucher/suspended" onClick={closeAll}>
                                        Suspended Vouchers
                                    </NavItem>
                                </ShowIf>
                                <ShowIf permission="creditnote:view">
                                    <NavItem to="/creditnote" onClick={closeAll}>
                                        Credit Notes
                                    </NavItem>
                                </ShowIf>
                                <ShowIf permission="debitnote:view">
                                    <NavItem to="/debitnote" onClick={closeAll}>
                                        Debit Notes
                                    </NavItem>
                                </ShowIf>
                                <ShowIf permission="expense:view">
                                    <NavItem to="/accountreports/expenses" onClick={closeAll}>
                                        Expenses
                                    </NavItem>
                                </ShowIf>
                            </NavDropdown>
                        </>

                        {/* ── Reports ── */}
                        <>
                            <NavDropdown
                                label="Reports"
                                isOpen={openDrop === "Reports"}
                                onToggle={toggle}
                            >
                                {/* Quick links moved from the top bar */}
                                <ShowIf permission="dashboard:view">
                                    <NavItem to="/dashboard" onClick={closeAll}>
                                        <i className="bi bi-speedometer2 me-2" />
                                        Dashboard
                                    </NavItem>
                                </ShowIf>
                                <ShowIf
                                    any={[
                                        "salesreturn:approve",
                                        "design:approve",
                                        "matching:approve",
                                        "cataloguematching:approve",
                                        "yarn_inventory:approve",
                                        "stockadjustment:approve",
                                        "qrstockadjustment:approve",
                                        "qrstockdeduction:approve",
                                    ]}
                                >
                                    <NavItem to="/approvals" onClick={closeAll}>
                                        <i className="bi bi-check2-square me-2" />
                                        Approvals
                                    </NavItem>
                                </ShowIf>
                                {/* Sales */}
                                <NavDropRight label="Sales Reports" any={["reports:salesbillregister", "reports:aging", "reports:gstsummary", "reports:partyledger", "reports:periodic"]}>
                                    <ShowIf permission="reports:salesbillregister">
                                        <NavItem to="/salesreports/bill-register" onClick={closeAll}>
                                            Sales Bill Register
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="reports:aging" condition={!restrictSeg}>
                                        <NavItem to="/salesreports/aging" onClick={closeAll}>
                                            Receivables Aging
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="reports:aging">
                                        <NavItem to="/salesreports/outstanding" onClick={closeAll}>
                                            Outstanding Report
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="reports:gstsummary">
                                        <NavItem to="/salesreports/gst-summary" onClick={closeAll}>
                                            GST Summary
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="reports:partyledger" condition={!restrictSeg}>
                                        <NavItem to="/salesreports/party-ledger" onClick={closeAll}>
                                            Party Ledger
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="reports:periodic">
                                        <NavItem to="/salesreports/periodic" onClick={closeAll}>
                                            Periodic Summary
                                        </NavItem>
                                    </ShowIf>
                                </NavDropRight>
                                {/* Operations */}
                                <NavDropRight label="Operations" any={["reports:challanregister", "reports:orderbook", "reports:paymentcollection", "reports:agentperformance", "reports:salesmanperformance"]}>
                                    <ShowIf permission="reports:challanregister">
                                        <NavItem to="/salesreports/challan-register" onClick={closeAll}>
                                            Challan Register
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="reports:orderbook">
                                        <NavItem to="/salesreports/order-book" onClick={closeAll}>
                                            Order Book
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="reports:paymentcollection">
                                        <NavItem to="/salesreports/payment-collection" onClick={closeAll}>
                                            Payment Collection
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="reports:agentperformance" condition={!restrictSeg}>
                                        <NavItem to="/salesreports/agent-performance" onClick={closeAll}>
                                            Agent Performance
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="reports:salesmanperformance">
                                        <NavItem to="/salesreports/salesman-performance" onClick={closeAll}>
                                            Salesman Performance
                                        </NavItem>
                                    </ShowIf>
                                </NavDropRight>
                                {/* Analytics */}
                                <NavDropRight label="Analytics" any={["reports:cataloguesales", "reports:partycataloguemsg", "reports:firmcomparison", "reports:fysummary"]}>
                                    <ShowIf permission="reports:cataloguesales">
                                        <NavItem to="/salesreports/catalogue-sales" onClick={closeAll}>
                                            Catalogue-wise Sales
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="reports:partycataloguemsg" condition={!restrictSeg}>
                                        <NavItem to="/salesreports/party-catalogue-message" onClick={closeAll}>
                                            Party Catalogue Message
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="reports:firmcomparison">
                                        <NavItem to="/salesreports/firm-comparison" onClick={closeAll}>
                                            Firm Comparison
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="reports:fysummary">
                                        <NavItem to="/salesreports/fy-summary" onClick={closeAll}>
                                            FY Summary
                                        </NavItem>
                                    </ShowIf>
                                </NavDropRight>
                                {/* Production */}
                                <NavDropRight label="Production" any={["reports:productionoutput", "reports:productionreport", "reports:seconds", "reports:endofday", "reports:goodsmovement"]}>
                                    <ShowIf permission="reports:productionoutput">
                                        <NavItem to="/salesreports/production-output" onClick={closeAll}>
                                            Production Output
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="reports:seconds">
                                        <NavItem to="/salesreports/seconds" onClick={closeAll}>
                                            Seconds (Defective)
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="reports:productionreport">
                                        <NavItem to="/salesreports/production-report" onClick={closeAll}>
                                            Production Report
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="reports:productionreport">
                                        <NavItem to="/salesreports/today-production" onClick={closeAll}>
                                            Today's Production
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="reports:productionreport">
                                        <NavItem to="/salesreports/orders-generated" onClick={closeAll}>
                                            Production Orders Generated
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="reports:endofday">
                                        <NavItem to="/salesreports/end-of-day" onClick={closeAll}>
                                            End-of-Day Summary
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="reports:goodsmovement">
                                        <NavItem to="/salesreports/goods-movement" onClick={closeAll}>
                                            Goods Movement
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="reports:clothrollproduction">
                                        <NavItem to="/salesreports/cloth-roll-production" onClick={closeAll}>
                                            Cloth Roll Production
                                        </NavItem>
                                    </ShowIf>
                                </NavDropRight>
                                {/* GST */}
                                <NavDropRight label="GST Reports" any={["gst:view", "gst:gstr1", "gst:gstr2", "gst:gstr3b", "gst:gstr9", "gst:reconciliation", "gst:ewaybill", "gst:tdstcs"]}>
                                    <ShowIf any={["gst:view", "gst:gstr1", "gst:gstr2", "gst:gstr3b", "gst:gstr9"]}>
                                        <NavItem to="/gstreports" onClick={closeAll}>
                                            GST Reports Hub
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="gst:gstr1">
                                        <NavItem to="/gstreports/gstr1" onClick={closeAll}>
                                            GSTR-1 (Outward)
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="gst:gstr2">
                                        <NavItem to="/gstreports/gstr2" onClick={closeAll}>
                                            GSTR-2 / ITC (Inward)
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="gst:gstr3b">
                                        <NavItem to="/gstreports/gstr3b" onClick={closeAll}>
                                            GSTR-3B (Summary)
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="gst:gstr9">
                                        <NavItem to="/gstreports/gstr9" onClick={closeAll}>
                                            GSTR-9 (Annual)
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="gst:reconciliation">
                                        <NavItem to="/gstreports/reconciliation" onClick={closeAll}>
                                            Reconciliation
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="gst:ewaybill">
                                        <NavItem to="/gstreports/eway-register" onClick={closeAll}>
                                            E-way Bill Register
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="gst:tdstcs">
                                        <NavItem to="/gstreports/tds-tcs" onClick={closeAll}>
                                            TDS / TCS Register
                                        </NavItem>
                                    </ShowIf>
                                </NavDropRight>
                                <NavDropRight label="Account Reports" any={["reports:payablesaging", "reports:supplierledger", "accounts:cashbook", "accounts:daybook", "accounts:receiptregister", "accounts:paymentregister", "firm_accounts:view", "accounts:trialbalance", "accounts:pnl", "accounts:balancesheet", "expense:view", "expense:openingbalance", "party_opening_balance:view", "journal_voucher:view", "ledger_account:view"]}>
                                    <ShowIf any={["reports:payablesaging", "reports:supplierledger", "accounts:cashbook", "accounts:daybook", "accounts:receiptregister", "accounts:paymentregister", "accounts:trialbalance", "accounts:pnl", "accounts:balancesheet", "expense:view", "expense:openingbalance", "party_opening_balance:view"]}>
                                        <NavItem to="/accountreports" onClick={closeAll}>
                                            Account Reports Hub
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="reports:payablesaging" condition={!restrictSeg}>
                                        <NavItem to="/accountreports/payables-aging" onClick={closeAll}>
                                            Payables Aging
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="reports:supplierledger" condition={!restrictSeg}>
                                        <NavItem to="/accountreports/supplier-ledger" onClick={closeAll}>
                                            Supplier Ledger
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="accounts:cashbook">
                                        <NavItem to="/accountreports/cash-book" onClick={closeAll}>
                                            Cash Book
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="accounts:daybook">
                                        <NavItem to="/accountreports/day-book" onClick={closeAll}>
                                            Day Book
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="accounts:receiptregister">
                                        <NavItem to="/accountreports/receipt-register" onClick={closeAll}>
                                            Receipt Register
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="accounts:paymentregister">
                                        <NavItem to="/accountreports/payment-register" onClick={closeAll}>
                                            Payment Register
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="firm_accounts:view">
                                        <NavItem to="/accountreports/bank-statement" onClick={closeAll}>
                                            Bank Statement
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="bank:reconcile">
                                        <NavItem to="/banking/reconciliation" onClick={closeAll}>
                                            Bank Reconciliation
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="mcsimport:view">
                                        <NavItem to="/accounts/mcs-import" onClick={closeAll}>
                                            MCS Import
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="cheque:manage">
                                        <NavItem to="/banking/cheques" onClick={closeAll}>
                                            Cheque Register
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="payment_run:create">
                                        <NavItem to="/banking/payment-run" onClick={closeAll}>
                                            Payment Run
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="reminders:manage">
                                        <NavItem to="/receivables/reminders" onClick={closeAll}>
                                            Payment Reminders
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="interest:view">
                                        <NavItem to="/receivables/interest" onClick={closeAll}>
                                            Interest on Late Payment
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="commission:view">
                                        <NavItem to="/receivables/commission" onClick={closeAll}>
                                            Agent Commission
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="tds:view">
                                        <NavItem to="/compliance/tax" onClick={closeAll}>
                                            Tax &amp; Compliance
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="journal_voucher:view">
                                        <NavItem to="/journalvoucher" onClick={closeAll}>
                                            Journal Vouchers
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="ledger_account:view">
                                        <NavItem to="/ledger-accounts" onClick={closeAll}>
                                            Chart of Accounts
                                        </NavItem>
                                    </ShowIf>
                                    <li><hr className="dropdown-divider" /></li>
                                    <ShowIf permission="accounts:trialbalance">
                                        <NavItem to="/accountreports/trial-balance" onClick={closeAll}>
                                            Trial Balance
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="accounts:pnl">
                                        <NavItem to="/accountreports/profit-loss" onClick={closeAll}>
                                            Profit &amp; Loss
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="accounts:balancesheet">
                                        <NavItem to="/accountreports/balance-sheet" onClick={closeAll}>
                                            Balance Sheet
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="accounts:pnl">
                                        <NavItem to="/accountreports/cash-flow" onClick={closeAll}>
                                            Cash Flow
                                        </NavItem>
                                        <NavItem to="/accountreports/ratios" onClick={closeAll}>
                                            Ratios
                                        </NavItem>
                                        <NavItem to="/accountreports/consolidated" onClick={closeAll}>
                                            Consolidated
                                        </NavItem>
                                        <NavItem to="/accountreports/stock-valuation" onClick={closeAll}>
                                            Stock Valuation
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="expense:openingbalance">
                                        <NavItem to="/accountreports/opening-balance" onClick={closeAll}>
                                            Opening Balances
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="party_opening_balance:view" condition={!restrictSeg}>
                                        <NavItem to="/accountreports/party-opening-balance" onClick={closeAll}>
                                            Party Opening Balances
                                        </NavItem>
                                    </ShowIf>
                                </NavDropRight>
                            </NavDropdown>
                        </>

                        {/* ── Dispatch ── */}
                        <>
                            <NavDropdown
                                label="Dispatch"
                                isOpen={openDrop === "Dispatch"}
                                onToggle={toggle}
                            >
                                <NavDropRight label="Registers" any={["outwardregister:view", "outwardchallan:view", "outwardworktype:view", "inwardregister:view"]}>
                                    <ShowIf permission="outwardregister:view">
                                        <NavItem to="/outwardregister" onClick={closeAll}>
                                            Outward Register
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="outwardregister:approve">
                                        <NavItem to="/outwardregister/deliveries" onClick={closeAll}>
                                            Pending Delivery
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="outwardchallan:view">
                                        <NavItem to="/outwardchallan" onClick={closeAll}>
                                            Outward Challan
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="outwardworktype:view">
                                        <NavItem to="/outwardworktype" onClick={closeAll}>
                                            Outward Work Types
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="inwardregister:view">
                                        <NavItem to="/inwardregister" onClick={closeAll}>
                                            Inward Register
                                        </NavItem>
                                    </ShowIf>
                                </NavDropRight>
                                <NavDropRight label="Transfers & Adjustments" any={["internaltransfer:view", "stockadjustment:view", "inwardregister:view", "outwardregister:view", "vendorstockadjustment:view"]}>
                                    <ShowIf permission="internaltransfer:view">
                                        <NavItem to="/internaltransfer" onClick={closeAll}>
                                            Internal Transfer
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="stockadjustment:view">
                                        <NavItem to="/stockadjustment" onClick={closeAll}>
                                            Stock Adjustment
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf all={["inwardregister:view", "outwardregister:view"]}>
                                        <NavItem to="/vendorregister" onClick={closeAll}>
                                            Vendor Register
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="vendorstockadjustment:view">
                                        <NavItem to="/vendorstockadjustment" onClick={closeAll}>
                                            Vendor Stock Adjustment
                                        </NavItem>
                                    </ShowIf>
                                </NavDropRight>
                                <NavDropRight label="QR Dispatch" any={["replenishment:view", "qrinternaltransfer:view", "qroutwardregister:view", "qrinwardregister:view", "qrstockadjustment:view", "qrstockdeduction:view", "qraudit:view", "qrscantimer:create", "qrscantimer:update", "qroutwardregister:approve"]}>
                                <ShowIf permission="replenishment:view">
                                    <NavItem
                                        to="/replenishment"
                                        onClick={closeAll}
                                    >
                                        Shop Replenishment
                                    </NavItem>
                                </ShowIf>
                                <ShowIf permission="qrinternaltransfer:view">
                                    <NavItem
                                        to="/qrinternaltransfer"
                                        onClick={closeAll}
                                    >
                                        QR Internal Transfer
                                    </NavItem>
                                </ShowIf>
                                <ShowIf permission="qroutwardregister:view">
                                    <NavItem
                                        to="/qroutwardregister"
                                        onClick={closeAll}
                                    >
                                        QR Outward Register
                                    </NavItem>
                                </ShowIf>
                                <ShowIf permission="qroutwardregister:approve">
                                    <NavItem
                                        to="/qroutwardregister/deliveries"
                                        onClick={closeAll}
                                    >
                                        QR Pending Delivery
                                    </NavItem>
                                </ShowIf>
                                <ShowIf permission="qrinwardregister:view">
                                    <NavItem
                                        to="/qrinwardregister"
                                        onClick={closeAll}
                                    >
                                        QR Inward Register
                                    </NavItem>
                                </ShowIf>
                                <ShowIf permission="qrstockadjustment:view">
                                    <NavItem to="/qrstockadjustment" onClick={closeAll}>
                                        QR Adjustment
                                    </NavItem>
                                </ShowIf>
                                <ShowIf permission="qrstockdeduction:view">
                                    <NavItem to="/qrstockdeduction" onClick={closeAll}>
                                        QR Deduction
                                    </NavItem>
                                </ShowIf>
                                <ShowIf permission="qraudit:view">
                                    <NavItem to="/qraudit" onClick={closeAll}>
                                        QR Audit
                                    </NavItem>
                                </ShowIf>
                                <ShowIf permission="qraudit:view">
                                    <NavItem to="/qrlocationaudit/add" onClick={closeAll}>
                                        QR Location Audit
                                    </NavItem>
                                </ShowIf>
                                <ShowIf permission="qrscantimer:create">
                                    <NavItem to="/qrscantimer" onClick={closeAll}>
                                        QR Scan Timer
                                    </NavItem>
                                </ShowIf>
                                <ShowIf permission="qrscantimer:update">
                                    <NavItem to="/qrscantimer/mark-voided" onClick={closeAll}>
                                        Mark Unscanned Voided
                                    </NavItem>
                                </ShowIf>
                                </NavDropRight>
                            </NavDropdown>
                        </>

                        {/* ── Inventory ── */}
                        <>
                            <NavDropdown
                                label="Inventory"
                                isOpen={openDrop === "Inventory"}
                                onToggle={toggle}
                            >
                                <NavDropRight label="Stock View" any={["inventory:view", "rack:view", "rackallocation:view", "rackallocation:update"]}>
                                    <ShowIf permission="inventory:view">
                                        <NavItem to="/inventory/by-location" onClick={closeAll}>
                                            By Location
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="inventory:view">
                                        <NavItem to="/inventory/by-design" onClick={closeAll}>
                                            By Design
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="inventory:view">
                                        <NavItem to="/inventory/by-stage" onClick={closeAll}>
                                            By Stage
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="inventory:view">
                                        <NavItem to="/inventory/at-vendors" onClick={closeAll}>
                                            At Vendors
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="inventory:view">
                                        <NavItem to="/inventory/production-pipeline" onClick={closeAll}>
                                            Production Pipeline
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf any={["rack:view", "rackallocation:view", "rackallocation:update"]}>
                                        <NavItem to="/rack" onClick={closeAll}>
                                            Racks
                                        </NavItem>
                                    </ShowIf>
                                </NavDropRight>
                                <NavDropRight label="Processing" any={["stitchtemplate:view", "blousestitch:view", "lehengarecipe:view", "inwarddyeing:view", "inwardstitching:view", "dyeingrecipe:view", "dyeing:view"]}>
                                    <ShowIf permission="stitchtemplate:view">
                                        <NavItem to="/stitchtemplate" onClick={closeAll}>
                                            Stitch Templates
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="blousestitch:view">
                                        <NavItem to="/blousestitch" onClick={closeAll}>
                                            Blouse Stitch
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="lehengarecipe:view">
                                        <NavItem to="/lehengarecipe" onClick={closeAll}>
                                            Lehenga Stitching Recipe
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="inwardstitching:view">
                                        <NavItem to="/inwardstitching" onClick={closeAll}>
                                            Inward From Stitching
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="inwarddyeing:view">
                                        <NavItem to="/inwarddyeing" onClick={closeAll}>
                                            Inward From Dyeing
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="dyeingrecipe:view">
                                        <NavItem to="/dyeingrecipe" onClick={closeAll}>
                                            Dyeing Recipes
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="dyeing:view">
                                        <NavItem to="/dyeing" onClick={closeAll}>
                                            Dyeing
                                        </NavItem>
                                    </ShowIf>
                                </NavDropRight>
                                <NavDropRight label="QR" any={["unitqr:view", "bundle:view", "qrmigration:view", "stockregrade:view"]}>
                                    <ShowIf permission="unitqr:view">
                                        <NavItem to="/unitqr" onClick={closeAll}>
                                            Unit QR List
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="unitqr:view">
                                        <NavItem to="/unitqr/scan" onClick={closeAll}>
                                            Scan QR
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="unitqr:view">
                                        <NavItem to="/unitqr/generated-qr" onClick={closeAll}>
                                            Generated QR
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="bundle:view">
                                        <NavItem to="/bundle/qr" onClick={closeAll}>
                                            QR Bundles
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="qrmigration:view">
                                        <NavItem to="/qrmigration" onClick={closeAll}>
                                            QR Migration
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="stockregrade:view">
                                        <NavItem to="/stockregrade" onClick={closeAll}>
                                            Regrade Stock
                                        </NavItem>
                                    </ShowIf>
                                </NavDropRight>
                            </NavDropdown>
                        </>

                        {/* ── Saree ── */}
                        {/* <>
                            <NavDropdown
                                label="Saree"
                                isOpen={openDrop === "Saree"}
                                onToggle={toggle}
                            >
                                <ShowIf permission="loosesareeorder:view">
                                    <NavItem
                                        to="/loosesareeorder"
                                        onClick={closeAll}
                                    >
                                        Loose Saree Orders
                                    </NavItem>
                                </ShowIf>
                                <ShowIf permission="sareestock:view">
                                    <NavItem
                                        to="/saree/stock"
                                        onClick={closeAll}
                                    >
                                        Saree Stock
                                    </NavItem>
                                </ShowIf>
                            </NavDropdown>
                        </> */}


                        {/* ── Masters ── */}
                        <>
                            <NavDropdown
                                label="Masters"
                                isOpen={openDrop === "Masters"}
                                onToggle={toggle}
                            >
                                <NavDropRight label="Parties" openLeft any={["party:view", "agent:view", "salesman:view"]}>
                                    <ShowIf permission="party:view">
                                        <NavItem to="/party" onClick={closeAll}>
                                            Party
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="party:update" condition={!hasPermission("party:restrict_sensitive")}>
                                        <NavItem to="/party/bulk-contacts" onClick={closeAll}>
                                            Bulk Party Contacts
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="party:view">
                                        <NavItem to="/pendingparty" onClick={closeAll}>
                                            Pending Party
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="agent:view">
                                        <NavItem to="/agent" onClick={closeAll}>
                                            Agent
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="salesman:view">
                                        <NavItem to="/salesman" onClick={closeAll}>
                                            Salesman
                                        </NavItem>
                                    </ShowIf>
                                </NavDropRight>
                                <NavDropRight label="Setup" openLeft any={["bank:view", "firm:view", "businessunit:view", "transport:view", "location:view", "hsncode:view"]}>
                                    <ShowIf permission="bank:view">
                                        <NavItem to="/bank" onClick={closeAll}>
                                            Bank
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="firm:view">
                                        <NavItem to="/firm" onClick={closeAll}>
                                            Firm
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="businessunit:view">
                                        <NavItem to="/businessunit" onClick={closeAll}>
                                            Business Units
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="transport:view">
                                        <NavItem to="/transport" onClick={closeAll}>
                                            Transport
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="location:view">
                                        <NavItem to="/location" onClick={closeAll}>
                                            Location
                                        </NavItem>
                                    </ShowIf>
                                    <ShowIf permission="hsncode:view">
                                        <NavItem to="/hsncode" onClick={closeAll}>
                                            HSN Code
                                        </NavItem>
                                    </ShowIf>
                                </NavDropRight>
                            </NavDropdown>
                        </>

                        {/* ── Auth ── */}
                        {!loggedIn && (
                            <li className="nav-item">
                                <Link
                                    className={`nav-link ${styles.dropBtn}`}
                                    to="/login"
                                    onClick={closeAll}
                                >
                                    Login
                                </Link>
                            </li>
                        )}
                        {loggedIn && (
                            <NavDropdown
                                name="Profile"
                                label={<SessionBadge fallback="Profile" />}
                                isOpen={openDrop === "Profile"}
                                onToggle={toggle}
                                right
                            >
                                <NavItem to="/change-password" onClick={closeAll}>Change Password</NavItem>
                                <NavItem to="/docs" onClick={closeAll}>Docs</NavItem>
                                <ShowIf any={["roles:view", "users:view", "accessmodule:view", "keyperson:view"]}>
                                    <li><hr className="dropdown-divider" /></li>
                                    <ShowIf permission="roles:view">
                                        <NavItem to="/roles" onClick={closeAll}>Role</NavItem>
                                    </ShowIf>
                                    <ShowIf permission="users:view">
                                        <NavItem to="/users" onClick={closeAll}>User</NavItem>
                                    </ShowIf>
                                    <ShowIf permission="jobs:manage">
                                        <NavItem to="/admin/jobs" onClick={closeAll}>Scheduled Jobs</NavItem>
                                    </ShowIf>
                                    <ShowIf permission="analytics:view">
                                        <NavItem to="/admin/analytics" onClick={closeAll}>Usage Analytics</NavItem>
                                    </ShowIf>
                                    <ShowIf permission="keyperson:view">
                                        <NavItem to="/admin/key-person" onClick={closeAll}>Key-person Risk</NavItem>
                                    </ShowIf>
                                    <ShowIf permission="accessmodule:view">
                                        <NavItem to="/accessmodule" onClick={closeAll}>Access Module</NavItem>
                                    </ShowIf>
                                </ShowIf>
                                <li><hr className="dropdown-divider" /></li>
                                <li>
                                    <Link
                                        className="dropdown-item"
                                        to="/"
                                        onClick={logout}
                                    >
                                        Log Out
                                    </Link>
                                </li>
                            </NavDropdown>
                        )}
                    </ul>
                </div>
            </div>
        </nav>
        </ActiveTrailContext.Provider>
    );
};

export default Nav;
